import { useEffect, useRef, useMemo } from 'react';
import { Logger } from '../config/logging/logger';

interface RecaptchaV2Props {
  onVerify: (token: string) => void;
  onExpired?: () => void;
}

interface RecaptchaV2Parameters {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
}

export default function RecaptchaV2({ onVerify, onExpired }: RecaptchaV2Props) {
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null); // store rendered widget ID
  const isMountedRef = useRef<boolean>(true); // track if component is mounted
  const onVerifyRef = useRef(onVerify);
  const onExpiredRef = useRef(onExpired);
  const logger = useMemo(() => Logger.create('RecaptchaV2'), []);

  // Keep callback refs up to date
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpiredRef.current = onExpired;
  }, [onVerify, onExpired]);

  useEffect(() => {
    // Reset mounted flag on mount
    isMountedRef.current = true;

    const renderRecaptcha = () => {
      window.grecaptcha?.enterprise.ready(() => {
        // Check if component is still mounted before proceeding
        if (!isMountedRef.current) {
          return;
        }

        if (!recaptchaRef.current) {
          logger.error('Recaptcha container not ready');
          return;
        }

        // Only render if widget not yet created
        if (widgetIdRef.current === null) {
          widgetIdRef.current = window.grecaptcha?.enterprise.render(
            recaptchaRef.current,
            {
              sitekey: process.env.REACT_APP_GOOGLE_RECAPTCHA_V2_SITE_KEY || '',
              callback: (token: string) => {
                logger.debug('✅ reCAPTCHA v2 token:', token);
                onVerifyRef.current(token);
              },
              'expired-callback': () => {
                logger.warn('⏱️ reCAPTCHA v2 token expired');
                onExpiredRef.current?.();
              },
            } as RecaptchaV2Parameters,
          ) as number;
        }
      });
    };

    const existingScript = document.getElementById('recaptcha-script');
    if (!existingScript) {
      // Load script if not present
      const script = document.createElement('script');
      script.id = 'recaptcha-script';
      script.src = 'https://www.google.com/recaptcha/enterprise.js';
      script.async = true;
      script.defer = true;
      script.onload = renderRecaptcha;
      document.body.appendChild(script);
    } else if (window.grecaptcha) {
      // Script already loaded, render immediately
      try {
        renderRecaptcha();
      } catch (e) {
        logger.error(e);
      }
    }

    return () => {
      // Mark component as unmounted to prevent queued callbacks from executing
      isMountedRef.current = false;
      // Reset widget ID so a new widget can be created on remount
      widgetIdRef.current = null;
    };
  }, [logger]);

  // show badge when recaptcha is rendered, hide when recaptcha is unmounted
  useEffect(() => {
    document.body.classList.add('show-recaptcha');
    document.body.classList.remove('hide-recaptcha');

    return () => {
      document.body.classList.remove('show-recaptcha');
      document.body.classList.add('hide-recaptcha');
    };
  }, []);

  return (
    <div
      ref={recaptchaRef}
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '0 0 30px',
      }}
    />
  );
}
